import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Flame, Mail, ArrowRight, Facebook, Download, Instagram } from 'lucide-react';
import { BRAND, NAV } from '../mock/mock';
import { useToast } from '../hooks/use-toast';
import { subscribe as subscribeApi } from '../lib/apiClient';

const Footer = () => {
  const [email, setEmail] = useState('');
  const { toast } = useToast();

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email || !/^\S+@\S+\.\S+$/.test(email)) {
      toast({ title: 'Please enter a valid email', description: 'We’d love to keep you close to the fire.' });
      return;
    }
    try {
      await subscribeApi(email);
      toast({ title: 'Welcome to the movement', description: 'You’re on the list. Watch for After the Fire™ updates.' });
      setEmail('');
    } catch (err) {
      toast({ title: 'Something went wrong', description: 'Please try again in a moment.' });
    }
  };

  return (
    <footer className="ember-texture border-t border-fire-gold/15 pt-16 pb-8">
      <div className="max-w-[1400px] mx-auto px-5 lg:px-8">
        {/* Newsletter */}
        <div className="card-dark rounded-2xl px-6 md:px-12 py-10 md:py-12 mb-14 text-center relative overflow-hidden">
          <div className="absolute inset-0 opacity-[0.07] pointer-events-none" style={{ backgroundImage: 'radial-gradient(circle at 50% 0%, #d97524, transparent 60%)' }} />
          <Flame className="w-9 h-9 text-fire-gold mx-auto mb-4 animate-flicker" strokeWidth={1.4} />
          <h3 className="font-display text-2xl md:text-3xl text-fire-cream mb-2">Stay Close to the Fire</h3>
          <p className="text-fire-cream/65 font-body max-w-xl mx-auto mb-6 text-sm md:text-base">
            Encouragement, event dates, new music and first access to the Flame Shop — straight to your inbox.
          </p>
          <form onSubmit={subscribe} className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Your email address"
              className="flex-1 bg-fire-ink/70 border border-fire-gold/25 rounded-full px-5 py-3 text-fire-cream placeholder:text-fire-cream/40 focus:outline-none focus:border-fire-gold transition-colors"
            />
            <button type="submit" className="btn-gold rounded-full px-6 py-3 font-semibold inline-flex items-center justify-center gap-2">
              Join <ArrowRight className="w-4 h-4" />
            </button>
          </form>
        </div>

        <div className="grid md:grid-cols-4 gap-10 mb-12">
          <div className="md:col-span-2">
            <div className="flex items-center gap-2.5 mb-4">
              <Flame className="w-6 h-6 text-fire-gold" strokeWidth={1.6} />
              <span className="font-marcellus tracking-[0.28em] text-fire-cream text-sm">AFTER THE <span className="text-gradient-gold">FIRE</span></span>
            </div>
            <p className="font-cormorant text-xl text-fire-goldlight italic mb-3">{BRAND.tagline}</p>
            <p className="text-fire-cream/60 font-body text-sm max-w-md leading-relaxed">{BRAND.declaration}</p>
          </div>

          <div>
            <h4 className="font-marcellus tracking-widest text-fire-gold text-xs mb-4">EXPLORE</h4>
            <ul className="space-y-2.5">
              {NAV.map((n) => (
                <li key={n.path}>
                  <Link to={n.path} className="text-fire-cream/65 hover:text-fire-goldlight font-body text-sm transition-colors">
                    {n.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="font-marcellus tracking-widest text-fire-gold text-xs mb-4">CONNECT</h4>
            <a href={`mailto:${BRAND.email}`} className="inline-flex items-center gap-2 text-fire-cream/70 hover:text-fire-goldlight font-body text-sm transition-colors mb-4">
              <Mail className="w-4 h-4" /> {BRAND.email}
            </a>
            <a
              href="https://www.facebook.com/profile.php?id=100072453218316"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-facebook-link"
              className="flex items-center gap-2 text-fire-cream/70 hover:text-fire-goldlight font-body text-sm transition-colors mb-4"
            >
              <Facebook className="w-4 h-4" /> Follow on Facebook
            </a>
            <a
              href="https://www.instagram.com/_thefirelady/"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-instagram-link"
              className="flex items-center gap-2 text-fire-cream/70 hover:text-fire-goldlight font-body text-sm transition-colors mb-4"
            >
              <Instagram className="w-4 h-4" /> Follow on Instagram
            </a>
            <p className="text-fire-cream/50 font-body text-sm mb-4">{BRAND.site}</p>
            <Link to="/book" className="btn-outline-gold rounded-full px-5 py-2.5 text-xs font-semibold inline-block">
              Book Dr. Gee
            </Link>
            <button
              onClick={() => window.dispatchEvent(new Event('atf-open-install'))}
              data-testid="footer-get-app"
              className="mt-3 flex items-center gap-2 text-fire-cream/65 hover:text-fire-goldlight font-body text-sm transition-colors"
            >
              <Download className="w-4 h-4" /> Get the App
            </button>
          </div>
        </div>

        <div className="gold-hairline mb-6" />
        <div className="flex flex-col md:flex-row items-center justify-between gap-3">
          <p className="text-fire-cream/40 font-body text-xs">
            © {new Date().getFullYear()} After the Fire™ • Dr. Mischell “Dr. Gee” Gee. All rights reserved.
          </p>
          <div className="flex items-center gap-5">
            <Link to="/terms" className="text-fire-cream/45 hover:text-fire-goldlight font-body text-xs transition-colors">Terms &amp; Conditions</Link>
            <span className="text-fire-cream/20">|</span>
            <Link to="/privacy" className="text-fire-cream/45 hover:text-fire-goldlight font-body text-xs transition-colors">Privacy Policy</Link>
            <span className="text-fire-cream/20">|</span>
            <Link to="/admin" className="text-fire-cream/45 hover:text-fire-goldlight font-body text-xs transition-colors">Admin</Link>
          </div>
        </div>
        <p className="text-fire-cream/30 font-body text-[11px] max-w-2xl mx-auto text-center mt-5">
          Fireside Clarity Calls provide peer, lived-experience support and are not therapy, medical, legal or crisis services.
        </p>
      </div>
    </footer>
  );
};

export default Footer;
